import { type FormSaveErrors } from '../../form-system/models';
import { ApiErrorType } from './ApiErrorType';
import { type ErrorContext, ErrorContextOf } from './ErrorContext';

export interface ApiError {
	error: ApiErrorType;
	isClientFetchError?: boolean;
	traceId?: string;
	status?: number;
	requestName?: string;
}

export interface ClientApiError extends ApiError {
	error: ApiErrorType.client;
}

export interface ClientWithDetailsApiError extends ApiError {
	error: ApiErrorType.clientWithDetails;
	context: ErrorContext;
	details: string;
}

export interface InvalidJsonApiError extends ApiError {
	error: ApiErrorType.json;
	details: string;
}

export interface JwtApiError extends ApiError {
	error: ApiErrorType.jwt;
	details: string;
}

export interface UnknownApiError extends ApiError {
	error: ApiErrorType.unknown;
	message: string;
	status: number;
	responseJson: any;
}

export interface JiraErrorCollectionResponse {
	errorMessages: string[];
	errors: {
		[field: string]: string | undefined;
	};
}

export function constructApiError(
	error: string,
	status: number,
	responseJson: any,
	requestName?: string,
	context?: string,
	contextId?: string,
	details?: string,
	isClientFetchError?: boolean,
	traceId?: string,
): ApiError {
	switch (error) {
		case ApiErrorType.client:
			return {
				error: ApiErrorType.client,
				isClientFetchError,
				traceId,
				status,
				requestName,
			} as ClientApiError;
		case ApiErrorType.clientWithDetails:
			return {
				error: ApiErrorType.clientWithDetails,
				context: ErrorContextOf(context),
				details: details || '',
				isClientFetchError,
				traceId,
				status,
				requestName,
			} as ClientWithDetailsApiError;
		case ApiErrorType.json:
			return {
				error: ApiErrorType.json,
				details: details || '',
				isClientFetchError,
				traceId,
				status,
				requestName,
			} as InvalidJsonApiError;
		case ApiErrorType.jwt:
			return {
				error: ApiErrorType.jwt,
				details: details || '',
				isClientFetchError,
				traceId,
				status,
				requestName,
			} as JwtApiError;
		default:
			return {
				error: ApiErrorType.unknown,
				message: error,
				status: status,
				responseJson,
				isClientFetchError,
				traceId,
				requestName,
			} as UnknownApiError;
	}
}

export const isUnknownApiError = (apiError: ApiError): apiError is UnknownApiError =>
	apiError.error === ApiErrorType.unknown;

export const getFormSaveErrors = (apiError: ApiError): FormSaveErrors | undefined => {
	if (isUnknownApiError(apiError) && apiError.status === 400) {
		return { generalErrors: [], errorsByQuestionId: {}, ...apiError.responseJson };
	}

	return undefined;
};

export const getJiraErrorCollectionResponse = (
	apiError: ApiError,
): JiraErrorCollectionResponse | undefined => {
	if (isUnknownApiError(apiError) && apiError.status >= 400 && apiError.status < 500) {
		return { errorMessages: [], errors: {}, ...apiError.responseJson };
	}

	return undefined;
};
